import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

export const MedicalProfileItemSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  notes: z.string().nullable().optional(),
});

export type MedicalProfileItem = z.infer<typeof MedicalProfileItemSchema>;

const MedicalProfileDTOSchema = z.object({
  id: z.string().uuid().nullable(),
  patientId: z.string().uuid(),
  allergies: z.array(MedicalProfileItemSchema),
  chronicConditions: z.array(MedicalProfileItemSchema),
  surgicalHistory: z.array(MedicalProfileItemSchema),
  updatedAt: z.string().nullable(),
});

export type MedicalProfileDTO = z.infer<typeof MedicalProfileDTOSchema>;

const MedicalProfileResponseSchema = z.object({
  success: z.boolean(),
  data: MedicalProfileDTOSchema,
});

export function useMedicalProfile(patientId?: string) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["medical-profile", patientId],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: `/api/v1/patients/${patientId}/medical-profile` });
      return MedicalProfileResponseSchema.parse(json).data;
    },
    enabled: !!patientId,
  });
}

export function useUpsertMedicalProfile() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      patientId,
      ...body
    }: {
      patientId: string;
      allergies?: MedicalProfileItem[];
      chronicConditions?: MedicalProfileItem[];
      surgicalHistory?: MedicalProfileItem[];
    }) => {
      const json = await backend({ method: "PUT", url: `/api/v1/patients/${patientId}/medical-profile`, data: body });
      return MedicalProfileResponseSchema.parse(json).data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["medical-profile", data.patientId] });
    },
  });
}
